import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { MicroLearningAPI } from '../../apis/micro-learning.api';
import {
  LearningModule,
  UserModuleProgress,
  LearningDifficulty
} from '../../components/MicroLearning/types';
import {
  BookOpen,
  Target,
  Clock,
  Users,
  TrendingUp,
  Star,
  Check,
  Plus,
  X,
  Search,
  RefreshCw,
  Trash2,
  Flame,
  Award,
  Sparkles,
  ChevronRight,
  Filter,
  Compass,
  GraduationCap,
  Brain
} from 'lucide-react';
import twygsLearnLogo from '../../assets/clay_twygs_logo.png';

// ============================================================================
// SERIES CARD COMPONENT
// ============================================================================
interface SeriesCardProps {
  series: LearningSeries;
  onAddToPath: (seriesId: string, difficulty: LearningDifficulty) => void;
  onRemoveFromPath: (seriesId: string) => void;
  isSaving: boolean;
  isRecentlyAdded: boolean;
}

const SeriesCard: React.FC<SeriesCardProps> = ({
  series,
  onAddToPath,
  onRemoveFromPath,
  isSaving,
  isRecentlyAdded
}) => {
  const [selectedDifficulty, setSelectedDifficulty] = useState<LearningDifficulty | null>(
    series.selectedDifficulty ||
      (series.availableDifficulties.length > 0 ? series.availableDifficulties[0] : null)
  );

  const handleAddToPath = () => {
    if (selectedDifficulty) {
      onAddToPath(series.id, selectedDifficulty);
    }
  };

  const getDifficultyColor = (difficulty: string, isSelected: boolean) => {
    if (!isSelected)
      return 'bg-white border-gray-200 text-gray-600 hover:border-learn-sage-300 hover:bg-learn-sage-50';
    switch (difficulty) {
      case 'BEGINNER':
        return 'bg-learn-sage-100 border-learn-sage-400 text-learn-sage-800';
      case 'INTERMEDIATE':
        return 'bg-learn-honey-100 border-learn-honey-400 text-learn-honey-800';
      case 'ADVANCED':
        return 'bg-learn-coral-100 border-learn-coral-400 text-learn-coral-800';
      default:
        return 'bg-learn-sage-100 border-learn-sage-400 text-learn-sage-800';
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`group relative bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
        series.isSelected
          ? 'border-learn-sage-300 shadow-lg shadow-learn-sage-100/50'
          : 'border-gray-100 hover:border-learn-sage-200 hover:shadow-xl'
      }`}
    >
      {/* Accent bar */}
      <div
        className={`h-1 ${series.isSelected ? 'bg-gradient-to-r from-learn-sage-400 to-learn-sage-500' : 'bg-gradient-to-r from-learn-sky-300 to-learn-sage-300'}`}
      />

      <div className="p-4 sm:p-5">
        {/* Header */}
        <div className="mb-3">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="text-base sm:text-lg font-bold text-gray-900 leading-tight flex-1">
              {series.title}
            </h4>
            {series.isSelected && (
              <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-learn-sage-700 bg-learn-sage-100 px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                <Check className="w-3 h-3" />
                In Path
              </span>
            )}
          </div>
          <span className="inline-block text-[10px] sm:text-xs font-medium text-learn-sky-700 bg-learn-sky-50 px-2 py-0.5 rounded-full mb-2">
            {series.category}
          </span>
          <p className="text-xs sm:text-sm text-gray-600 leading-relaxed line-clamp-2">
            {series.description}
          </p>
        </div>

        {/* Stats - Horizontal row */}
        <div className="flex items-center justify-around gap-1 py-2.5 sm:py-3 px-2 bg-gray-50 rounded-xl mb-4">
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1">
              <BookOpen className="w-3.5 h-3.5 text-learn-sage-500" />
              <span className="text-base font-bold text-gray-800">{series.totalModules}</span>
            </div>
            <span className="text-[9px] sm:text-[10px] text-gray-500 uppercase">modules</span>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5 text-learn-sky-500" />
              <span className="text-base font-bold text-gray-800 whitespace-nowrap">
                {series.estimatedTotalTime}
              </span>
            </div>
            <span className="text-[9px] sm:text-[10px] text-gray-500 uppercase">time</span>
          </div>
          <div className="w-px h-8 bg-gray-200" />
          <div className="flex flex-col items-center">
            <div className="flex items-center gap-1">
              <Users className="w-3.5 h-3.5 text-learn-honey-500" />
              <span className="text-base font-bold text-gray-800">{series.totalCompletions}</span>
            </div>
            <span className="text-[9px] sm:text-[10px] text-gray-500 uppercase">learners</span>
          </div>
        </div>

        {/* Difficulty Selection */}
        <div className="mb-4">
          <label className="block text-[10px] sm:text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">
            Select Level
          </label>
          <div className="flex gap-1.5 sm:gap-2">
            {series.availableDifficulties.map((difficulty) => (
              <button
                key={difficulty}
                onClick={() => setSelectedDifficulty(difficulty)}
                className={`flex-1 px-2 sm:px-3 py-2 rounded-lg sm:rounded-xl border-2 text-[10px] sm:text-xs font-bold transition-all duration-200 ${getDifficultyColor(
                  difficulty,
                  selectedDifficulty === difficulty
                )}`}
              >
                {difficulty === 'BEGINNER'
                  ? 'Begin'
                  : difficulty === 'INTERMEDIATE'
                    ? 'Inter'
                    : 'Adv'}
                <span className="hidden sm:inline">
                  {difficulty === 'BEGINNER'
                    ? 'ner'
                    : difficulty === 'INTERMEDIATE'
                      ? 'mediate'
                      : 'anced'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Action Button */}
        <div>
          {series.isSelected ? (
            <button
              onClick={() => onRemoveFromPath(series.id)}
              disabled={isSaving}
              className="w-full px-3 py-2.5 sm:py-3 text-learn-coral-700 bg-learn-coral-50 border-2 border-learn-coral-200 rounded-xl text-sm font-semibold hover:bg-learn-coral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              {isSaving ? 'Removing...' : 'Remove'}
            </button>
          ) : (
            <button
              onClick={handleAddToPath}
              disabled={isSaving || !selectedDifficulty}
              className="w-full px-3 py-2.5 sm:py-3 bg-gradient-to-r from-learn-sage-500 to-learn-sage-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-learn-sage-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {isSaving ? 'Adding...' : 'Add to Path'}
            </button>
          )}
        </div>

        {/* Success Message */}
        <AnimatePresence>
          {isRecentlyAdded && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              className="overflow-hidden"
            >
              <div className="p-2.5 sm:p-3 bg-gradient-to-r from-learn-sage-50 to-green-50 border border-learn-sage-200 rounded-lg flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-learn-sage-600 flex-shrink-0" />
                <p className="text-xs sm:text-sm font-semibold text-learn-sage-700">
                  Added! Check your feed
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// ============================================================================
// CONSTANTS
// ============================================================================
const TAB_CONST = {
  PATHS: 'paths',
  PROGRESS: 'progress',
  DISCOVER: 'discover'
};

const TAB_LIST = [
  { id: 1, name: 'My Learning', value: TAB_CONST.PATHS, icon: GraduationCap },
  { id: 2, name: 'Progress', value: TAB_CONST.PROGRESS, icon: TrendingUp },
  { id: 3, name: 'Discover', value: TAB_CONST.DISCOVER, icon: Compass }
];

// ============================================================================
// TYPES
// ============================================================================
interface LearningSeries {
  id: string;
  title: string;
  description: string;
  category: string;
  totalModules: number;
  estimatedTotalTime: string;
  availableDifficulties: LearningDifficulty[];
  averageRating: number;
  totalCompletions: number;
  isSelected: boolean;
  selectedDifficulty: LearningDifficulty | null;
  createdAt: Date;
}

interface UserLearningPath {
  id: string;
  seriesId: string;
  selectedDifficulty: LearningDifficulty;
  series: {
    id: string;
    title: string;
    description: string;
    category: string;
    totalModules: number;
    completedModules: number;
    progressPercent: number;
    estimatedTotalTime: string;
  };
  addedAt: Date;
}

interface LearningStats {
  totalModulesCompleted: number;
  totalTimeSpent: number;
  currentStreak: number;
  averageScore: number;
  favoriteCategory: string;
  completionRate: number;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const Learning: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [availableSeries, setAvailableSeries] = useState<LearningSeries[]>([]);
  const [userLearningPaths, setUserLearningPaths] = useState<UserLearningPath[]>([]);
  const [userHistory, setUserHistory] = useState<UserModuleProgress[]>([]);
  const [userStats, setUserStats] = useState<LearningStats | null>(null);
  const [initialLoading, setInitialLoading] = useState(true); // Only for first load
  const [tabLoading, setTabLoading] = useState(false); // For tab switches
  const [savingSeriesIds, setSavingSeriesIds] = useState<Set<string>>(new Set()); // Track which series are being saved

  // Filter and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficultyFilter, setSelectedDifficultyFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'popular' | 'rating'>('newest');

  // Track recently added series for animation
  const [recentlyAddedSeriesId, setRecentlyAddedSeriesId] = useState<string | null>(null);

  // Restart confirmation dialog
  const [showRestartDialog, setShowRestartDialog] = useState(false);
  const [seriesToRestart, setSeriesToRestart] = useState<{ id: string; title: string } | null>(
    null
  );

  // Track if data has been loaded for each tab
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());

  const currentTab = searchParams.get('tab') || TAB_CONST.PATHS;

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value });
  };

  const getTitle = (tab: string) => {
    switch (tab) {
      case TAB_CONST.PATHS:
        return 'My Learning';
      case TAB_CONST.PROGRESS:
        return 'Learning Progress';
      case TAB_CONST.DISCOVER:
        return 'Discover';
      default:
        return 'Learning';
    }
  };

  // Load initial data on component mount
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load tab-specific data when tab changes (only if not already loaded)
  useEffect(() => {
    if (!loadedTabs.has(currentTab)) {
      loadTabData(currentTab, true);
    }
  }, [currentTab]);

  const loadInitialData = async () => {
    setInitialLoading(true);
    try {
      // Load paths first (for the default tab)
      const paths = await MicroLearningAPI.getUserLearningPaths();
      setUserLearningPaths(paths || []);
      setLoadedTabs((prev) => new Set([...prev, TAB_CONST.PATHS]));
    } catch (error) {
      console.error('Failed to load initial data:', error);
      setUserLearningPaths([]);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadTabData = async (tab: string, showLoading: boolean = false) => {
    if (showLoading) setTabLoading(true);

    try {
      if (tab === TAB_CONST.PATHS) {
        const paths = await MicroLearningAPI.getUserLearningPaths();
        setUserLearningPaths(paths || []);
      } else if (tab === TAB_CONST.PROGRESS) {
        const [history, stats] = await Promise.all([
          MicroLearningAPI.getUserHistory(),
          MicroLearningAPI.getUserStats()
        ]);
        setUserHistory(history || []);
        setUserStats(stats);
      } else if (tab === TAB_CONST.DISCOVER) {
        const series = await MicroLearningAPI.getAvailableSeries();
        setAvailableSeries(series || []);
      }

      setLoadedTabs((prev) => new Set([...prev, tab]));
    } catch (error) {
      console.error(`Failed to load ${tab} data:`, error);
    } finally {
      if (showLoading) setTabLoading(false);
    }
  };

  // Refresh all data silently (no loading states) - used after mutations
  const refreshAllData = async () => {
    try {
      const [paths, series] = await Promise.all([
        MicroLearningAPI.getUserLearningPaths(),
        MicroLearningAPI.getAvailableSeries()
      ]);

      setUserLearningPaths(paths || []);
      setAvailableSeries(series || []);

      // Also refresh progress data if it was loaded
      if (loadedTabs.has(TAB_CONST.PROGRESS)) {
        const [history, stats] = await Promise.all([
          MicroLearningAPI.getUserHistory(),
          MicroLearningAPI.getUserStats()
        ]);
        setUserHistory(history || []);
        setUserStats(stats);
      }
    } catch (error) {
      console.error('Failed to refresh data:', error);
    }
  };

  // Combined loading state for display
  const loading = initialLoading || tabLoading;

  const handleAddToLearningPath = async (seriesId: string, difficulty: LearningDifficulty) => {
    setSavingSeriesIds((prev) => new Set([...prev, seriesId]));
    setRecentlyAddedSeriesId(seriesId);

    try {
      await MicroLearningAPI.addToLearningPath(seriesId, difficulty);

      // Refresh all data silently
      await refreshAllData();

      window.dispatchEvent(new CustomEvent('learningPathsUpdated'));

      // Clear the success animation after 3 seconds
      setTimeout(() => {
        setRecentlyAddedSeriesId(null);
      }, 3000);
    } catch (error) {
      console.error('Failed to add series to learning path:', error);
      setRecentlyAddedSeriesId(null);
    } finally {
      setSavingSeriesIds((prev) => {
        const next = new Set(prev);
        next.delete(seriesId);
        return next;
      });
    }
  };

  const handleRemoveFromLearningPath = async (seriesId: string) => {
    setSavingSeriesIds((prev) => new Set([...prev, seriesId]));
    try {
      await MicroLearningAPI.removeFromLearningPath(seriesId);

      // Refresh all data silently
      await refreshAllData();

      window.dispatchEvent(new CustomEvent('learningPathsUpdated'));
    } catch (error) {
      console.error('Failed to remove series from learning path:', error);
    } finally {
      setSavingSeriesIds((prev) => {
        const next = new Set(prev);
        next.delete(seriesId);
        return next;
      });
    }
  };

  const handleRestartSeries = (seriesId: string, seriesTitle: string) => {
    setSeriesToRestart({ id: seriesId, title: seriesTitle });
    setShowRestartDialog(true);
  };

  const confirmRestartSeries = async () => {
    if (!seriesToRestart) return;

    const seriesId = seriesToRestart.id;
    setShowRestartDialog(false);
    setSavingSeriesIds((prev) => new Set([...prev, seriesId]));

    try {
      await MicroLearningAPI.restartLearningSeries(seriesId, 'User requested restart');

      // Refresh all data silently
      await refreshAllData();

      window.dispatchEvent(new CustomEvent('learningPathsUpdated'));
    } catch (error) {
      console.error('Failed to restart series:', error);
    } finally {
      setSavingSeriesIds((prev) => {
        const next = new Set(prev);
        next.delete(seriesId);
        return next;
      });
      setSeriesToRestart(null);
    }
  };

  const cancelRestartSeries = () => {
    setShowRestartDialog(false);
    setSeriesToRestart(null);
  };

  // ============================================================================
  // RENDER: LEARNING PATHS TAB
  // ============================================================================
  const renderLearningPathsTab = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-gradient-to-br from-learn-sage-50 via-white to-learn-sky-50 rounded-xl sm:rounded-2xl border border-learn-sage-100 p-4 sm:p-6"
      >
        <div className="absolute top-0 right-0 w-40 sm:w-64 h-40 sm:h-64 bg-gradient-to-bl from-learn-sage-100/30 to-transparent rounded-full -translate-y-20 sm:-translate-y-32 translate-x-20 sm:translate-x-32" />
        <div className="relative">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-learn-sage-400 to-learn-sage-500 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-learn-sage-200 flex-shrink-0">
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-xl font-bold text-gray-900">
                Your Learning Journey
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">Track and continue your series</p>
            </div>
          </div>
        </div>
      </motion.div>

      {loading ? (
        <div className="space-y-3 sm:space-y-4">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-100 animate-pulse"
            >
              <div className="h-4 sm:h-5 bg-gray-200 rounded-lg w-3/4 mb-3 sm:mb-4"></div>
              <div className="h-2 sm:h-3 bg-gray-100 rounded w-full mb-2"></div>
              <div className="h-2 sm:h-3 bg-gray-100 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      ) : userLearningPaths.length > 0 ? (
        <div className="space-y-4">
          {userLearningPaths.map((path, index) => {
            const progressPercent = path.series.progressPercent || 0;
            const isCompleted = progressPercent === 100;

            return (
              <motion.div
                key={path.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-2xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                  isCompleted ? 'border-learn-sage-200' : 'border-gray-100'
                }`}
              >
                {/* Progress bar at top */}
                <div className="h-1 bg-gray-100">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercent}%` }}
                    transition={{ duration: 1, delay: 0.3 }}
                    className={`h-full ${isCompleted ? 'bg-gradient-to-r from-learn-sage-400 to-green-500' : 'bg-gradient-to-r from-learn-sky-400 to-learn-sage-400'}`}
                  />
                </div>

                <div className="p-4 sm:p-5">
                  {/* Title and badges */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
                      {path.series.title}
                    </h4>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {path.series.completedModules > 0 && (
                        <button
                          onClick={() => handleRestartSeries(path.seriesId, path.series.title)}
                          disabled={savingSeriesIds.has(path.seriesId)}
                          className="p-1.5 sm:p-2 text-learn-honey-600 bg-learn-honey-50 hover:bg-learn-honey-100 rounded-lg transition-colors disabled:opacity-50"
                          title="Restart series"
                        >
                          <RefreshCw
                            className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${savingSeriesIds.has(path.seriesId) ? 'animate-spin' : ''}`}
                          />
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveFromLearningPath(path.seriesId)}
                        disabled={savingSeriesIds.has(path.seriesId)}
                        className="p-1.5 sm:p-2 text-learn-coral-600 bg-learn-coral-50 hover:bg-learn-coral-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove from path"
                      >
                        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Badges row */}
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <span className="text-[10px] sm:text-xs font-semibold text-learn-sky-700 bg-learn-sky-50 px-2 py-0.5 rounded-full">
                      {path.selectedDifficulty.charAt(0) +
                        path.selectedDifficulty.slice(1).toLowerCase()}
                    </span>
                    {path.series.category && (
                      <span className="text-[10px] sm:text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                        {path.series.category}
                      </span>
                    )}
                    {isCompleted && (
                      <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-semibold text-learn-sage-700 bg-learn-sage-100 px-2 py-0.5 rounded-full">
                        <Award className="w-3 h-3" />
                        Complete
                      </span>
                    )}
                  </div>

                  <p className="text-xs sm:text-sm text-gray-600 mb-3 line-clamp-2">
                    {path.series.description}
                  </p>

                  {/* Progress section */}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3 text-xs sm:text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3.5 h-3.5 text-learn-sage-500" />
                          {path.series.totalModules}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5 text-learn-sky-500" />
                          {path.series.estimatedTotalTime}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm sm:text-base font-bold text-learn-sage-600">
                          {progressPercent}%
                        </span>
                        <span className="text-[10px] text-gray-500">
                          ({path.series.completedModules}/{path.series.totalModules})
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${progressPercent}%` }}
                        transition={{ duration: 1, delay: 0.3 }}
                        className={`h-full rounded-full ${isCompleted ? 'bg-gradient-to-r from-learn-sage-400 to-green-500' : 'bg-gradient-to-r from-learn-sky-400 to-learn-sage-400'}`}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-12 sm:py-16 px-4 sm:px-6"
        >
          <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-learn-sage-100 to-learn-sky-100 rounded-3xl flex items-center justify-center">
            <BookOpen className="w-10 h-10 sm:w-12 sm:h-12 text-learn-sage-500" />
          </div>
          <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
            Start Your Learning Journey
          </h4>
          <p className="text-sm sm:text-base text-gray-600 mb-6 max-w-sm mx-auto">
            Discover learning series and add them to your path.
          </p>
          <button
            onClick={() => handleTabChange(TAB_CONST.DISCOVER)}
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-learn-sage-500 to-learn-sage-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-learn-sage-200 transition-all"
          >
            <Compass className="w-4 h-4 sm:w-5 sm:h-5" />
            Discover Series
          </button>
        </motion.div>
      )}
    </div>
  );

  // ============================================================================
  // RENDER: PROGRESS TAB
  // ============================================================================
  const renderProgressTab = () => (
    <div className="space-y-5 sm:space-y-6">
      {/* Stats Overview */}
      {userStats && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 gap-3 sm:gap-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0 }}
            className="bg-gradient-to-br from-learn-sage-50 to-white p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-learn-sage-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-learn-sage-100 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3">
              <Check className="w-4 h-4 sm:w-5 sm:h-5 text-learn-sage-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-learn-sage-700 mb-0.5">
              {userStats.totalModulesCompleted || 0}
            </div>
            <div className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Modules Done
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-learn-sky-50 to-white p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-learn-sky-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-learn-sky-100 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-learn-sky-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-learn-sky-700 mb-0.5">
              {(() => {
                const totalMinutes = Math.round((userStats.totalTimeSpent || 0) / 60);
                if (totalMinutes >= 60) {
                  const hours = Math.floor(totalMinutes / 60);
                  const mins = totalMinutes % 60;
                  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
                }
                return `${totalMinutes}m`;
              })()}
            </div>
            <div className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Time Learning
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-learn-honey-50 to-white p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-learn-honey-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-learn-honey-100 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3">
              <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-learn-honey-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-learn-honey-700 mb-0.5">
              {userStats.currentStreak || 0}
            </div>
            <div className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Day Streak
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-learn-coral-50 to-white p-4 sm:p-5 rounded-xl sm:rounded-2xl border border-learn-coral-100 hover:shadow-lg transition-shadow"
          >
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-learn-coral-100 rounded-lg sm:rounded-xl flex items-center justify-center mb-2 sm:mb-3">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-learn-coral-600" />
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-learn-coral-700 mb-0.5">
              {userStats.averageScore > 0 ? Math.round(userStats.averageScore) : 0}%
            </div>
            <div className="text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
              Avg Score
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Overall Progress */}
      {userStats && userStats.totalModulesCompleted > 0 && userStats.completionRate > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-learn-sage-500 to-learn-sage-600 p-4 sm:p-6 rounded-xl sm:rounded-2xl shadow-xl shadow-learn-sage-200/50 text-white"
        >
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center backdrop-blur-sm">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm sm:text-lg">Overall Progress</h4>
                <p className="text-learn-sage-100 text-xs sm:text-sm">Keep it up!</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl sm:text-3xl font-bold">
                {Math.round(userStats.completionRate)}%
              </div>
              <div className="text-[10px] sm:text-xs text-learn-sage-100 uppercase tracking-wider">
                Complete
              </div>
            </div>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 sm:h-3 overflow-hidden backdrop-blur-sm">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${userStats.completionRate}%` }}
              transition={{ duration: 1, delay: 0.5 }}
              className="bg-white h-2 sm:h-3 rounded-full shadow-lg"
            />
          </div>
        </motion.div>
      )}

      {/* Learning Activity */}
      <div>
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-1.5 sm:gap-2">
            <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-learn-sage-500" />
            Activity
          </h3>
          <span className="text-xs sm:text-sm text-gray-500">
            {userHistory.length} module{userHistory.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="space-y-3 sm:space-y-4">
            {[...Array(2)].map((_, i) => (
              <div
                key={i}
                className="bg-white p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-100 animate-pulse"
              >
                <div className="h-4 sm:h-5 bg-gray-200 rounded-lg w-1/2 mb-3 sm:mb-4"></div>
                <div className="h-2 sm:h-3 bg-gray-100 rounded w-3/4 mb-2"></div>
                <div className="h-2 sm:h-3 bg-gray-100 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : userHistory.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {(() => {
              // Group modules by series
              const seriesGroups = userHistory.reduce(
                (groups, progress) => {
                  const seriesId = progress.module?.seriesId || 'unknown';
                  const seriesTitle = progress.module?.series?.title || 'Unknown Series';

                  if (!groups[seriesId]) {
                    groups[seriesId] = {
                      seriesTitle,
                      seriesCategory:
                        progress.module?.series?.category || progress.module?.category,
                      totalModules: progress.module?.series?.totalModules || 0,
                      modules: []
                    };
                  }

                  groups[seriesId].modules.push(progress);
                  return groups;
                },
                {} as Record<string, any>
              );

              return Object.entries(seriesGroups).map(([seriesId, group], groupIndex) => {
                const completedCount = group.modules.filter((m) => m.status === 'COMPLETED').length;
                const totalCount = group.totalModules || group.modules.length;
                const progressPercent = Math.round((completedCount / totalCount) * 100);

                return (
                  <motion.div
                    key={seriesId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: groupIndex * 0.1 }}
                    className="bg-white rounded-xl sm:rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
                  >
                    {/* Series Header */}
                    <div className="p-3 sm:p-5 border-b border-gray-100 bg-gradient-to-r from-learn-sage-50/50 to-learn-sky-50/50">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-bold text-sm sm:text-base text-gray-900 truncate mb-1">
                            {group.seriesTitle}
                          </h4>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] sm:text-xs font-medium text-learn-sky-700 bg-learn-sky-100 px-2 py-0.5 rounded-full">
                              {group.seriesCategory}
                            </span>
                            <span className="text-[10px] sm:text-xs text-gray-500">
                              {group.modules.length} tracked
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-lg sm:text-2xl font-bold text-learn-sage-600">
                            {progressPercent}%
                          </div>
                          <div className="text-[10px] sm:text-xs text-gray-500">
                            {completedCount}/{totalCount}
                          </div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2 overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progressPercent}%` }}
                          transition={{ duration: 0.8 }}
                          className="bg-gradient-to-r from-learn-sage-400 to-learn-sage-500 h-1.5 sm:h-2 rounded-full"
                        />
                      </div>
                    </div>

                    {/* Modules */}
                    <div className="divide-y divide-gray-50">
                      {group.modules
                        .sort((a, b) => (a.module?.seriesOrder || 0) - (b.module?.seriesOrder || 0))
                        .slice(0, 5)
                        .map((progress) => {
                          const isCompleted = progress.status === 'COMPLETED';
                          const isInProgress = progress.status === 'IN_PROGRESS';

                          return (
                            <div
                              key={progress.id}
                              className={`p-3 sm:p-4 hover:bg-gray-50/50 transition-colors ${isCompleted ? 'bg-learn-sage-50/30' : ''}`}
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div
                                  className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                    isCompleted
                                      ? 'bg-learn-sage-100'
                                      : isInProgress
                                        ? 'bg-learn-sky-100'
                                        : 'bg-gray-100'
                                  }`}
                                >
                                  {isCompleted ? (
                                    <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-learn-sage-600" />
                                  ) : isInProgress ? (
                                    <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-learn-sky-600" />
                                  ) : (
                                    <span className="text-[10px] sm:text-xs font-bold text-gray-400">
                                      {progress.module?.seriesOrder}
                                    </span>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-medium text-gray-900 text-xs sm:text-sm truncate">
                                    {progress.module?.title}
                                  </h5>
                                  <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                                    <span>#{progress.module?.seriesOrder}</span>
                                    {progress.completedAt && (
                                      <>
                                        <span>•</span>
                                        <span className="text-learn-sage-600">Completed</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-300" />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </motion.div>
                );
              });
            })()}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-10 sm:py-16 px-4 sm:px-6 bg-gradient-to-br from-learn-sky-50/50 to-learn-sage-50/50 rounded-xl sm:rounded-2xl border border-gray-100"
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gradient-to-br from-learn-sky-100 to-learn-sage-100 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <TrendingUp className="w-8 h-8 sm:w-10 sm:h-10 text-learn-sky-500" />
            </div>
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No Activity Yet</h4>
            <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6 max-w-sm mx-auto">
              Start learning to track your progress!
            </p>
            <button
              onClick={() => handleTabChange(TAB_CONST.DISCOVER)}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-learn-sage-500 to-learn-sage-600 text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
            >
              <Compass className="w-4 h-4 sm:w-5 sm:h-5" />
              Discover Series
            </button>
          </motion.div>
        )}
      </div>
    </div>
  );

  // ============================================================================
  // RENDER: DISCOVER TAB
  // ============================================================================
  const renderDiscoverTab = () => {
    const categories = ['all', ...new Set(availableSeries.map((s) => s.category))];

    let filteredSeries = availableSeries.filter((series) => {
      if (
        searchQuery &&
        !series.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !series.description.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }
      if (selectedCategory !== 'all' && series.category !== selectedCategory) {
        return false;
      }
      if (
        selectedDifficultyFilter !== 'all' &&
        !series.availableDifficulties.includes(selectedDifficultyFilter as LearningDifficulty)
      ) {
        return false;
      }
      return true;
    });

    filteredSeries = filteredSeries.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return b.totalCompletions - a.totalCompletions;
        case 'rating':
          return b.averageRating - a.averageRating;
        case 'newest':
        default:
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
    });

    return (
      <div className="space-y-4 sm:space-y-6">
        {/* Search & Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-learn-sky-50 via-white to-learn-sage-50 rounded-xl sm:rounded-2xl border border-learn-sky-100 p-4 sm:p-6"
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-learn-sky-400 to-learn-sage-400 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg shadow-learn-sky-200/50 flex-shrink-0">
              <Compass className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h2 className="text-base sm:text-xl font-bold text-gray-900">Discover Series</h2>
              <p className="text-xs sm:text-sm text-gray-600">Find your next adventure</p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-3 sm:mb-4">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search series..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-10 py-2.5 sm:py-3 bg-white border-2 border-gray-100 rounded-lg sm:rounded-xl focus:border-learn-sage-300 focus:ring-4 focus:ring-learn-sage-100 outline-none transition-all text-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
          </div>

          {/* Filters */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div>
              <label className="block text-[10px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-1.5 uppercase tracking-wide">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-2 sm:px-4 py-2 sm:py-2.5 bg-white border-2 border-gray-100 rounded-lg sm:rounded-xl focus:border-learn-sage-300 outline-none transition-all text-xs sm:text-sm font-medium"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All' : cat}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-1.5 uppercase tracking-wide">
                Level
              </label>
              <select
                value={selectedDifficultyFilter}
                onChange={(e) => setSelectedDifficultyFilter(e.target.value)}
                className="w-full px-2 sm:px-4 py-2 sm:py-2.5 bg-white border-2 border-gray-100 rounded-lg sm:rounded-xl focus:border-learn-sage-300 outline-none transition-all text-xs sm:text-sm font-medium"
              >
                <option value="all">All</option>
                <option value="BEGINNER">Begin</option>
                <option value="INTERMEDIATE">Inter</option>
                <option value="ADVANCED">Adv</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] sm:text-xs font-semibold text-gray-600 mb-1 sm:mb-1.5 uppercase tracking-wide">
                Sort
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="w-full px-2 sm:px-4 py-2 sm:py-2.5 bg-white border-2 border-gray-100 rounded-lg sm:rounded-xl focus:border-learn-sage-300 outline-none transition-all text-xs sm:text-sm font-medium"
              >
                <option value="newest">New</option>
                <option value="popular">Popular</option>
                <option value="rating">Top</option>
              </select>
            </div>
          </div>

          {/* Results */}
          <div className="mt-3 sm:mt-4 flex items-center justify-between">
            <p className="text-xs sm:text-sm text-gray-600">
              <span className="font-bold text-learn-sage-600">{filteredSeries.length}</span> of{' '}
              {availableSeries.length}
            </p>
            {(searchQuery || selectedCategory !== 'all' || selectedDifficultyFilter !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                  setSelectedDifficultyFilter('all');
                }}
                className="text-xs sm:text-sm font-medium text-learn-coral-600 hover:text-learn-coral-700 flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Clear
              </button>
            )}
          </div>
        </motion.div>

        {/* Series Grid */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="bg-white p-5 rounded-2xl border border-gray-100 animate-pulse"
              >
                <div className="h-5 bg-gray-200 rounded-lg w-3/4 mb-4"></div>
                <div className="h-3 bg-gray-100 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-100 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredSeries.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {filteredSeries.map((series, _index) => (
              <SeriesCard
                key={series.id}
                series={series}
                onAddToPath={handleAddToLearningPath}
                onRemoveFromPath={handleRemoveFromLearningPath}
                isSaving={savingSeriesIds.has(series.id)}
                isRecentlyAdded={recentlyAddedSeriesId === series.id}
              />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-10 sm:py-16 px-4 sm:px-6"
          >
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center">
              <Search className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
            </div>
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">No Series Found</h4>
            <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
              Try adjusting your filters
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedDifficultyFilter('all');
              }}
              className="px-5 sm:px-6 py-2.5 sm:py-3 bg-learn-sage-100 text-learn-sage-700 rounded-xl text-sm font-semibold hover:bg-learn-sage-200 transition-all"
            >
              Clear Filters
            </button>
          </motion.div>
        )}
      </div>
    );
  };

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  return (
    <div className="min-h-[calc(100vh-80px)] bg-gradient-to-b from-learn-sage-50/30 via-white to-learn-sky-50/30">
      <Helmet>
        <title>{getTitle(currentTab)} | Twygs Learning</title>
      </Helmet>

      <div className="max-w-4xl mx-auto px-3 sm:px-6 pb-8 sm:pb-10">
        {/* Header */}
        <div className="pt-5 sm:pt-8 pb-4 sm:pb-6">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <img src={twygsLearnLogo} alt="Twygs Learn" className="h-14 sm:h-[80px] w-auto" />
          </motion.div>
        </div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="sticky top-[60px] sm:top-[68px] md:top-[80px] z-20 -mx-3 sm:-mx-6 px-3 sm:px-6 py-2 bg-white/95 backdrop-blur-xl border-b border-gray-100"
        >
          <div className="flex bg-gray-100 rounded-full p-1 sm:bg-transparent sm:p-0 sm:gap-1">
            {TAB_LIST.map((item) => {
              const Icon = item.icon;
              const isActive = currentTab === item.value;

              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.value)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 sm:py-2.5 rounded-full sm:rounded-xl text-xs sm:text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-white sm:bg-gradient-to-r sm:from-learn-sage-500 sm:to-learn-sage-600 text-learn-sage-700 sm:text-white shadow-sm sm:shadow-lg sm:shadow-learn-sage-200/50'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'sm:text-white' : ''}`} />
                  <span className="hidden sm:inline">{item.name}</span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Tab Content */}
        <div className="pt-4 sm:pt-6">
          <AnimatePresence mode="wait">
            {currentTab === TAB_CONST.PATHS && (
              <motion.div
                key="paths"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {renderLearningPathsTab()}
              </motion.div>
            )}
            {currentTab === TAB_CONST.PROGRESS && (
              <motion.div
                key="progress"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {renderProgressTab()}
              </motion.div>
            )}
            {currentTab === TAB_CONST.DISCOVER && (
              <motion.div
                key="discover"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {renderDiscoverTab()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Restart Confirmation Dialog */}
      <AnimatePresence>
        {showRestartDialog && seriesToRestart && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
              onClick={cancelRestartSeries}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="w-14 h-14 mx-auto mb-4 bg-learn-honey-100 rounded-2xl flex items-center justify-center">
                  <RefreshCw className="w-7 h-7 text-learn-honey-600" />
                </div>

                <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                  Restart Series?
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  This will reset all progress in{' '}
                  <span className="font-semibold text-gray-900">"{seriesToRestart.title}"</span>
                </p>

                <div className="flex gap-3">
                  <button
                    onClick={cancelRestartSeries}
                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRestartSeries}
                    disabled={seriesToRestart ? savingSeriesIds.has(seriesToRestart.id) : false}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-learn-honey-500 to-learn-honey-600 text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${seriesToRestart && savingSeriesIds.has(seriesToRestart.id) ? 'animate-spin' : ''}`}
                    />
                    {seriesToRestart && savingSeriesIds.has(seriesToRestart.id)
                      ? 'Restarting...'
                      : 'Restart'}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Learning;
